import { useAtomValue } from "jotai";
import { useCallback } from "react";
import { TextBlock, TextEnabledElement } from "shared/datamodel/schemas";
import { elementDataLayerSchema } from "shared/datamodel/schemas/element-data-layer";
import { placeTextBlock, TextBlockInitOptions } from "shared/datamodel/textBlock";
import { syncServiceAtom, undoRedoStackAtom } from "state-atoms";
import {
  DynamicElement,
  getFieldText,
  getFieldSize,
  getGroupIdForNewField,
  isElementCentered,
  getDerivedTextBlockPositions,
  findNextAvailableFieldPosition,
} from "../dynamic-data-utils";
import { canvasElementPrefix } from "shared/util/utils";
import { ReadTransaction } from "@workcanvas/reflect";
import { Node, NodeConfig } from "konva/types/Node";
import { useFeatureFlag } from "frontend/hooks/use-feature-flag/use-feature-flag";
import consts from "shared/consts";
import { getDerivedGroupId } from "utils/node-utils";
import tracking from "frontend/tracking";

export function useDynamicElementActions() {
  const isDataLayerEnabled = useFeatureFlag("element-data-layer");

  const syncService = useAtomValue(syncServiceAtom);
  const reflect = syncService?.getReplicache();
  const undoRedoStack = useAtomValue(undoRedoStackAtom);

  const initializeDataLayer = useCallback(
    async (elementId: string) => {
      if (!isDataLayerEnabled) return;

      await undoRedoStack?.patchElement(elementId, (element) => {
        element.dataLayer ??= elementDataLayerSchema.parse({});

        // These two fields are optional in the schema for legacy reasons.
        element.dataLayer.elementMapping ??= {};
        element.dataLayer.integrations ??= {};
      });
    },
    [undoRedoStack, isDataLayerEnabled]
  );

  const getDerivedTextIds = useCallback(
    async (elementId: string, integrationId?: string, itemId?: string) => {
      if (!isDataLayerEnabled) return [];

      const derivedTextIds: string[] =
        (await reflect?.query(async function (tx: ReadTransaction) {
          const prefix = `${canvasElementPrefix}textBlock-`;
          let allTextBlocks = (await tx.scan({ prefix }).entries().toArray()) as [string, TextBlock][];
          allTextBlocks = allTextBlocks.filter(([_, textBlock]) => {
            if (textBlock?.hidden) return false;

            const text = textBlock.text;
            if (!integrationId) return text.includes(`${elementId}:integration:`);
            if (!itemId) return text.includes(`${elementId}:integration:${integrationId}:`);
            return text.includes(`${elementId}:integration:${integrationId}:${itemId}:`);
          });

          const ids = allTextBlocks.map(([id]) => id.replace(canvasElementPrefix, ""));
          return ids;
        })) ?? [];
      return derivedTextIds;
    },
    [reflect, isDataLayerEnabled]
  );

  const addIntegrationToElement = useCallback(
    async (
      elementId: string,
      integrationId: string,
      itemId: string,
      options: {
        clearText?: boolean;
        replace?: boolean;
        sequenceId?: string;
      } = {}
    ) => {
      if (!isDataLayerEnabled) return;
      const { clearText = false, replace = false, sequenceId } = options;

      tracking.trackAnalyticsEvent("data-layer-integration-added", {
        elementId,
        integrationId,
        itemId,
      });

      await undoRedoStack?.patchElement(
        elementId,
        (element) => {
          element.dataLayer ??= elementDataLayerSchema.parse({});
          element.dataLayer.integrations ??= {};
          element.dataLayer.elementMapping ??= {};
          element.dataLayer.rules ??= [];

          if (clearText) {
            (element as TextEnabledElement).text = "";
          }

          if (replace) {
            element.dataLayer.integrations[integrationId] = { [itemId]: { columnIds: "all" } };

            const mappingKeys = Object.keys(element.dataLayer.elementMapping);
            for (const key of mappingKeys) {
              const [, mappingIntegrationId, , columnId] = key.split(":");
              if (mappingIntegrationId !== integrationId) continue;

              const newItemId = `integration:${integrationId}:${itemId}`;
              element.dataLayer.elementMapping[`${newItemId}:${columnId}`] = element.dataLayer.elementMapping[key];
              delete element.dataLayer.elementMapping[key];
            }

            // Change rules to look at the new integration item
            element.dataLayer.rules = element.dataLayer.rules.map((rule) => {
              if (!rule.itemId?.startsWith("integration")) return rule;

              const [, ruleIntegrationId, , columnId] = rule.fieldId.split(":");
              if (ruleIntegrationId !== integrationId) return rule;

              const newItemId = `integration:${integrationId}:${itemId}`;
              return { ...rule, itemId: newItemId, fieldId: `${newItemId}:${columnId}` };
            });
          } else {
            element.dataLayer.integrations[integrationId] ??= {};
            element.dataLayer.integrations[integrationId][itemId] = { columnIds: "all" };
          }
        },
        sequenceId
      );

      if (replace) {
        // Replace fields that reference the old integration item
        const derivedTextIds = await getDerivedTextIds(elementId, integrationId);
        await undoRedoStack?.patchElements(
          derivedTextIds ?? [],
          (element) => {
            const [shapeId, key, _oldIntegrationId, _oldItemId, columnId] = (element as DynamicElement).text.split(":");
            (element as DynamicElement).text = `${shapeId}:${key}:${integrationId}:${itemId}:${columnId}`;
          },
          sequenceId
        );
      }
    },
    [undoRedoStack, getDerivedTextIds, isDataLayerEnabled]
  );

  const removeIntegrationFromElement = useCallback(
    async (elementId: string, integrationId: string, itemId: string, options: { sequenceId?: string } = {}) => {
      if (!isDataLayerEnabled) return;

      const { sequenceId: externalSequenceId } = options;
      const sequenceId = externalSequenceId ?? `remove-integration-item-${integrationId}-${itemId}`;

      tracking.trackAnalyticsEvent("data-layer-integration-removed", {
        elementId,
        integrationId,
        itemId,
      });

      if (!externalSequenceId) undoRedoStack?.startSequence(sequenceId);

      await undoRedoStack?.patchElement(
        elementId,
        (element) => {
          if (!element.dataLayer) return;

          const integration = element.dataLayer.integrations?.[integrationId];
          if (integration) {
            if (integration[itemId]) {
              delete integration[itemId];
            }
            if (Object.keys(integration).length === 0) {
              delete element.dataLayer.integrations?.[integrationId];
            }
          }

          // Remove rules that reference the integration item
          element.dataLayer.rules = element.dataLayer.rules.filter(
            (rule) => rule.itemId !== `integration:${integrationId}:${itemId}`
          );
        },
        sequenceId
      );

      // Delete text blocks that reference the integration item
      const derivedTextIds = await getDerivedTextIds(elementId, integrationId, itemId);

      await undoRedoStack?.patchElements(
        derivedTextIds ?? [],
        (element) => {
          element.hidden = true;
        },
        sequenceId
      );

      if (!externalSequenceId) undoRedoStack?.endSequence(sequenceId);
    },
    [undoRedoStack, getDerivedTextIds, isDataLayerEnabled]
  );

  const addFieldToElement = useCallback(
    async (
      elementId: string,
      element: DynamicElement,
      integrationId: string,
      itemId: string,
      columnId: string,
      options: {
        sequenceId?: string;
        fieldValue?: string;
        position?: { x: number; y: number };
        textProperties?: TextBlockInitOptions;
      } = {}
    ) => {
      if (!isDataLayerEnabled) return;

      const { sequenceId: externalSequenceId, fieldValue, position, textProperties } = options;
      const sequenceId = externalSequenceId ?? `add-field-to-element-${elementId}`;

      const fieldId = `integration:${integrationId}:${itemId}:${columnId}`;
      const fieldText = getFieldText(elementId, undefined, { integrationId, columnId, itemId });
      const maxWidth = element.width * element.scaleX;

      tracking.trackAnalyticsEvent("data-layer-field-added", {
        elementId,
        fieldId,
      });

      const { width, height } = getFieldSize(fieldValue, maxWidth, textProperties?.fontSize);

      const positions = await getDerivedTextBlockPositions(elementId, reflect);
      const newPosition = position ?? findNextAvailableFieldPosition(positions ?? [], element, height);
      if (isElementCentered(element)) {
        newPosition.x -= width / 2;
        newPosition.y -= height / 2;
      }

      const { groupId, groupHistory, updateParentGroup } = getGroupIdForNewField(element);
      const { id, textBlock } = placeTextBlock(
        { ...textProperties, width, groupId, groupHistory, derivedElementIds: [elementId], hidden: true },
        newPosition,
        fieldText
      );
      const newTextBlockId = `${textBlock.type}-${id}`;

      // This operation is not undoable, on purpose. It solves a lot of problems.
      // It's also transparent to the user since the new text block is hidden by default.
      await reflect?.mutate.putElement({ id: newTextBlockId, element: textBlock });

      if (!externalSequenceId) undoRedoStack?.startSequence(sequenceId);

      await undoRedoStack?.patchElement(
        newTextBlockId,
        (element) => {
          element.hidden = false;
        },
        sequenceId
      );

      await undoRedoStack?.patchElement(
        elementId,
        (element) => {
          if (!element.dataLayer) return;

          element.dataLayer.elementMapping ??= {};
          element.dataLayer.elementMapping[fieldId] = newTextBlockId;

          if (updateParentGroup) {
            element.groupId = groupId;
            element.groupHistory = groupHistory;
          }
        },
        sequenceId
      );

      if (!externalSequenceId) undoRedoStack?.endSequence(sequenceId);

      return newTextBlockId;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isDataLayerEnabled]
  );

  /**
   * This function returns the data layer element for a current selection.
   * It does not require the data layer to be enabled.
   */
  const getDynamicElementForSelection = useCallback((selection: Node<NodeConfig>[]): Node<NodeConfig> | null => {
    const parentNodes = selection.filter((node) => consts.ELEMENTS_SUPPORTING_DATA_LAYER.includes(node.attrs.type));
    if (parentNodes.length !== 1) return null;

    const parentNode = parentNodes[0];
    const parentGroupId = getDerivedGroupId(parentNode);
    if (!parentGroupId && selection.length > 1) return null;

    const onlyChildrenSelected = selection.every((node) => getDerivedGroupId(node) === parentGroupId);
    return onlyChildrenSelected ? parentNode : null;
  }, []);

  return {
    addIntegrationToElement,
    removeIntegrationFromElement,
    addFieldToElement,
    initializeDataLayer,
    getDynamicElementForSelection,
    getDerivedTextIds,
  };
}
